import { centsToUsdString, getOrdinal } from '@paperstac/helpers/lib/numberHelpers';
import { getNameFromCode } from './services/states';
import uniqWith from 'lodash/uniqWith';
import React from 'react';
import { connectCurrentRefinements } from 'react-instantsearch-dom';
import { ALGOLIA_RANGE_INPUT_MAX, ALGOLIA_RANGE_INPUT_MIN } from './AlgoliaRangeInput';
import Box from './Box';
import Button from './Button';
import Flex from './Flex';
import Heading from './Heading';
import XIcon from './icons/XIcon';
import UnstyledButton from './UnstyledButton';
const usdRangeLabel = _ref => {
  let {
    min,
    max
  } = _ref;
  if (min !== ALGOLIA_RANGE_INPUT_MIN && max !== ALGOLIA_RANGE_INPUT_MAX) return `${centsToUsdString(min, false)} to ${centsToUsdString(max, false)}`;
  if (min !== ALGOLIA_RANGE_INPUT_MIN && max === ALGOLIA_RANGE_INPUT_MAX) return `${centsToUsdString(min, false)} and higher`;
  if (min === ALGOLIA_RANGE_INPUT_MIN && max !== ALGOLIA_RANGE_INPUT_MAX) return `${centsToUsdString(max, false)} and lower`;
  return '';
};
const percentRangeLabel = _ref2 => {
  let {
    min,
    max
  } = _ref2;
  if (min !== ALGOLIA_RANGE_INPUT_MIN && max !== ALGOLIA_RANGE_INPUT_MAX) return `${min}% to ${max}%`;
  if (min !== ALGOLIA_RANGE_INPUT_MIN && max === ALGOLIA_RANGE_INPUT_MAX) return `${min}% and higher`;
  if (min === ALGOLIA_RANGE_INPUT_MIN && max !== ALGOLIA_RANGE_INPUT_MAX) return `${max}% and lower`;
  return '';
};
const rangeLabel = _ref3 => {
  let {
    min,
    max
  } = _ref3;
  if (min !== ALGOLIA_RANGE_INPUT_MIN && max !== ALGOLIA_RANGE_INPUT_MAX) return `${min} to ${max}`;
  if (min !== ALGOLIA_RANGE_INPUT_MIN && max === ALGOLIA_RANGE_INPUT_MAX) return `${min} and higher`;
  if (min === ALGOLIA_RANGE_INPUT_MIN && max !== ALGOLIA_RANGE_INPUT_MAX) return `${max} and lower`;
  return '';
};
const AlgoliaCurrentRefinements = /*#__PURE__*/React.memo(_ref4 => {
  let {
    items,
    prependRefinements = [],
    refine
  } = _ref4;
  const algoliaRefinements = uniqWith(items, (a, b) => a.id === b.id).reduce((refinements, item) => {
    switch (item.id) {
      case 'investmentMetaAgg.investmentToBalancePercent':
        return percentRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `ITB: ${percentRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'investmentMetaAgg.investmentToValuePercent':
        return percentRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `ITV: ${percentRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'isPool':
        return [...refinements, ...item.items.map(i => ({
          label: i.label === 'false' ? 'Single Asset' : 'Asset Pool',
          handleClear: () => refine(i.value)
        }))];
      case 'pendingSale':
        return [...refinements, ...item.items.map(i => ({
          label: i.label === 'false' ? 'Available' : 'Pending Sale',
          handleClear: () => refine(i.value)
        }))];
      case 'noteMetaAgg.isHardestHitFundState':
        return [...refinements, {
          label: 'Hardest Hit Fund State',
          handleClear: () => refine(item.value)
        }];
      case 'noteMetaAgg.isJudicialState':
        return [...refinements, {
          label: 'Judicial State',
          handleClear: () => refine(item.value)
        }];
      case 'noteMetaAgg.isNonJudicialState':
        return [...refinements, {
          label: 'Non-Judicial State',
          handleClear: () => refine(item.value)
        }];
      case 'noteMetaAgg.interestRatePercent':
        return percentRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `Interest Rate: ${percentRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'noteMetaAgg.legalStatus':
        return [...refinements, ...item.items.map(i => ({
          label: `Legal Status: ${i.label}`,
          handleClear: () => refine(i.value)
        }))];
      case 'noteMetaAgg.lienPosition':
        return [...refinements, ...item.items.map(i => ({
          label: `${getOrdinal(i.label)} Lien Position`,
          handleClear: () => refine(i.value)
        }))];
      case 'noteMetaAgg.loanToValuePercent':
        return percentRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `LTV: ${percentRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'noteMetaAgg.paymentsRemaining':
        return rangeLabel(item.currentRefinement) ? [...refinements, {
          label: `Payments Remaining: ${rangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'noteMetaAgg.propertyValue':
        return usdRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `Property Value: ${usdRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'noteMetaAgg.state':
        return [...refinements, ...item.items.map(i => ({
          label: `State: ${item.items.length > 10 ? i.label : getNameFromCode(i.label)}`,
          handleClear: () => refine(i.value)
        }))];
      case 'noteMetaAgg.upb':
        return usdRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `UPB: ${usdRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'listPrice':
        return usdRangeLabel(item.currentRefinement) ? [...refinements, {
          label: `Price: ${usdRangeLabel(item.currentRefinement)}`,
          handleClear: () => refine(item.value)
        }] : refinements;
      case 'uniqueSellerName':
        return [...refinements, ...item.items.map(i => ({
          label: `Seller: ${i.label.split('::')[1]}`,
          handleClear: () => refine(i.value)
        }))];
      default:
        return Array.isArray(item.items) ? [...refinements, ...item.items.map(i => ({
          label: i.label,
          handleClear: () => refine(i.value)
        }))] : refinements;
    }
  }, []);
  const refinements = [...prependRefinements, ...algoliaRefinements];
  const handleClearAll = () => refine(items);
  return !!refinements.length ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      mb: 5,
      p: 3,
      bg: 'highlight',
      border: 'default',
      borderRadius: 'corner'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    mb: 3
  }, /*#__PURE__*/React.createElement(Heading, {
    flex: "1 1 0",
    fontSize: 2
  }, "Current Filters"), /*#__PURE__*/React.createElement(UnstyledButton, {
    onClick: handleClearAll,
    sx: {
      color: 'gray.6',
      '&:hover,&:active': {
        color: 'red'
      }
    }
  }, /*#__PURE__*/React.createElement(XIcon, null))), refinements.map(_ref5 => {
    let {
      label,
      handleClear
    } = _ref5;
    return /*#__PURE__*/React.createElement(Button, {
      key: label,
      onClick: handleClear,
      size: "small",
      variant: "outline",
      sx: {
        borderRadius: '20px',
        fontWeight: 'normal',
        px: '12px',
        background: 'white'
      },
      mr: 2,
      mb: 2
    }, label, /*#__PURE__*/React.createElement(XIcon, {
      size: 10,
      fill: "red",
      ml: 2,
      sx: {
        position: 'relative',
        top: '-1px'
      }
    }));
  })) : null;
});
AlgoliaCurrentRefinements.propTypes = {};
AlgoliaCurrentRefinements.displayName = 'AlgoliaCurrentRefinements';
export default connectCurrentRefinements(AlgoliaCurrentRefinements);